import * as React from "react";
import {searchCampaignConfirms} from "./campaignConfirmsService";
import Loading from "../../components/loading";
import {
    Alert,
    FormControl,
    FormGroup,
    Input,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Pagination,
    Paper,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TableHeaderRow from "../../components/TableHeaderRow";
import StripedTableRow from "../../components/StripedTableRow";
import DateRange, {RangeToTimes} from "../../components/DateRange";
import useDebounce from "../../lib/UseDebounce";
import moment from "moment";
import {useParams} from "react-router-dom";

const oldUrl = process.env.REACT_APP_OLD_URL;

export default function CampaignConfirmsPage() {
    const {hpCampaignId: hpCampaignIdParam} = useParams();
    const hpCampaignId = parseInt(hpCampaignIdParam || "0", 10);

    const [items, setItems] = React.useState([] as any[]);
    const [loading, setLoading] = React.useState(true);
    const [skip, setSkip] = React.useState(0);
    const [limit, setLimit] = React.useState(100);
    const [search, setSearch] = React.useState("");
    const [count, setCount] = React.useState(0);
    const [page, setPage] = React.useState(1);
    const [dateRange, setDateRange] = React.useState(RangeToTimes("Month to Date") as any);

    React.useEffect(() => {
        document.title = `Campaign Confirms ${hpCampaignId} | DN`;
    }, [hpCampaignId]);

    const reload = async (
        nSkip: number,
        nLimit: number,
        sSearch: string,
        range: any,
    ) => {
        if (!hpCampaignId) {
            setItems([]);
            setCount(0);
            setLoading(false);
            return;
        }
        setLoading(true);
        const startDate = moment(range[0]).format("YYYY-MM-DD");
        const endDate = moment(range[1]).format("YYYY-MM-DD");
        const result = await searchCampaignConfirms(hpCampaignId, startDate, endDate, nSkip, nLimit, sSearch);
        setItems(result.items);
        setCount(result.count);
        setLoading(false);
    };

    React.useEffect(() => {
        reload(skip, limit, search, dateRange).then(() => {});
    }, []);

    const handleDateRange = (value: any) => {
        setDateRange(value);
        setSkip(0);
        setPage(1);
        reload(0, limit, search, value).then(() => {});
    };

    const handleLimit = async (event: SelectChangeEvent) => {
        const l = parseInt(event.target.value as string, 10);
        setLimit(l);
        setPage(1);
        setSkip(0);
        await reload(0, l, search, dateRange);
    };

    useDebounce(() => {
        setSkip(0);
        setPage(1);
        reload(0, limit, search, dateRange).then(() => {});
    }, [search], 400);

    const doPageChange = async (newPageNum: number) => {
        const newSkip = limit * (newPageNum - 1);
        setSkip(newSkip);
        setPage(newPageNum);
        await reload(newSkip, limit, search, dateRange);
    };

    const doSearch = (value: string) => {
        setSearch(value);
    };

    if (!hpCampaignId) {
        return <Alert severity="error">Invalid campaign id</Alert>;
    }

    return (
        <>
            <h2>Campaign Confirms — {hpCampaignId}</h2>
            <FormGroup row={true} sx={{alignItems: "center", flexWrap: "wrap", gap: 1}}>
                <FormControl sx={{m: 1, minWidth: 120}} size="small">
                    <InputLabel id="items-label">Items</InputLabel>
                    <Select
                        labelId="items-label"
                        value={limit.toString()}
                        label="Items"
                        onChange={handleLimit}
                        size="small"
                    >
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={250}>250</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                        <MenuItem value={1000}>1000</MenuItem>
                    </Select>
                </FormControl>
                <FormControl sx={{m: 1}} size="small">
                    <DateRange showTime={false} dateRange={dateRange} setDateRange={handleDateRange} style={{}}/>
                </FormControl>
                <FormControl variant="outlined" sx={{m: 1, minWidth: 120}} size="small">
                    <Input
                        id="search"
                        type="text"
                        spellCheck="false"
                        placeholder="Search"
                        value={search || ""}
                        onChange={(e: any) => doSearch(e.target.value)}
                        startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>}
                    />
                </FormControl>
            </FormGroup>
            {loading ? <Loading/> :
                <>
                    {!items || items.length === 0 ? <Alert severity="warning">No results found</Alert> :
                        <>
                            <Box sx={{margin: "10px 0", fontWeight: "bold", fontSize: "13px"}}>
                                Showing {(skip + 1).toLocaleString()} to {Math.min(limit + skip, count).toLocaleString()} of {count.toLocaleString()}.
                            </Box>
                            <TableContainer component={Paper} sx={{width: "100%", maxWidth: "100%", overflowX: "auto"}}>
                                <Table size="small" sx={{tableLayout: "fixed", width: "100%"}}>
                                    <TableHead>
                                        <TableHeaderRow>
                                            <TableCell sx={{width: "12%"}}>Lead</TableCell>
                                            <TableCell sx={{width: "16%"}}>Date</TableCell>
                                            <TableCell sx={{width: "18%"}}>Publisher</TableCell>
                                            <TableCell sx={{width: "40%"}}>Confirm</TableCell>
                                            <TableCell sx={{width: "14%"}}>Action</TableCell>
                                        </TableHeaderRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.map((row: any) => (
                                            <StripedTableRow key={`${row.leadId}-${row.dateSent}`}>
                                                <TableCell>
                                                    <Link
                                                        href={`/lead?combo_id=${row.leadId}${row.zip || ""}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {row.leadId}
                                                    </Link>
                                                </TableCell>
                                                <TableCell sx={{whiteSpace: "nowrap"}}>
                                                    {moment(row.dateSent).format("M/D/YYYY, h:mm A")}
                                                </TableCell>
                                                <TableCell sx={{wordBreak: "break-word"}}>{row.publisherName}</TableCell>
                                                <TableCell sx={{wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal"}}>
                                                    <code style={{fontSize: "12px", whiteSpace: "pre-wrap", wordBreak: "break-word"}}>{row.confirm}</code>
                                                </TableCell>
                                                <TableCell sx={{wordBreak: "break-word"}}>{row.action}</TableCell>
                                            </StripedTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box sx={{margin: "30px 0", fontWeight: "bold", fontSize: "13px", display: "inline-block"}}>
                                Showing {(skip + 1).toLocaleString()} to {Math.min(limit + skip, count).toLocaleString()} of {count.toLocaleString()}.
                            </Box>
                            <Box sx={{margin: "10px 0", fontWeight: "bold", fontSize: "13px", display: "inline-block", position: "relative", float: "right"}}>
                                <Pagination
                                    sx={{marginTop: "20px"}}
                                    count={Math.max(1, Math.ceil(count / limit))}
                                    onChange={(e, value) => doPageChange(value)}
                                    page={page}
                                    variant="outlined"
                                />
                            </Box>
                        </>
                    }
                </>
            }
        </>
    );
}
